import { useState } from 'react';
import { Check } from 'lucide-react';
import { toggleAudience, type ModuleAudience } from './moduleWrites';

export const AUDIENCE_OPTIONS: ReadonlyArray<{ value: ModuleAudience; label: string }> = [
  { value: 'affiliate', label: 'Affiliates / ISOs' },
  { value: 'customer', label: 'Tenants' },
  { value: 'employee', label: 'Tenant employees' },
];
const ALL = AUDIENCE_OPTIONS.map((o) => o.value);
const TENANT_AUDIENCES: ModuleAudience[] = ['customer', 'employee'];

export type ModuleStatus = 'draft' | 'published';

/** One-line summary for the collapsed card button, e.g. "Live · Tenants, Tenant employees". */
export function visibilitySummary(audience: ModuleAudience[], status: ModuleStatus): string {
  const names = AUDIENCE_OPTIONS.filter((o) => audience.includes(o.value)).map((o) => o.label);
  return `${status === 'published' ? 'Live' : 'Draft'} · ${names.join(', ') || 'No audience'}`;
}

/**
 * Who a module is for and whether it is live. Learn in platform-app / POS shows
 * a module only when it is Published AND has Tenants or Tenant employees.
 * Custom controls only (pills + segmented toggle), 44px targets, ruby on light.
 */
export default function ModuleVisibilityField({ audience, status, onAudience, onStatus }: {
  audience: ModuleAudience[];
  status: ModuleStatus;
  onAudience: (next: ModuleAudience[]) => void;
  onStatus: (next: ModuleStatus) => void;
}) {
  const [hint, setHint] = useState('');
  const inLearn = status === 'published' && audience.some((a) => TENANT_AUDIENCES.includes(a));

  const pill = (on: boolean) => `inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-full text-xs font-semibold border transition ${
    on ? 'bg-[#DC143C] border-[#DC143C] text-white hover:bg-[#9F1239]' : 'bg-white border-gray-300 text-gray-700 hover:border-[#DC143C] hover:text-[#9F1239]'
  }`;

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Who is it for</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Module audience">
          {AUDIENCE_OPTIONS.map((o) => {
            const on = audience.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  const next = toggleAudience(audience, o.value, ALL);
                  if (!next) { setHint('Keep at least one audience.'); return; }
                  setHint('');
                  onAudience(next);
                }}
                className={pill(on)}
              >
                {on && <Check className="w-3.5 h-3.5" />}
                {o.label}
              </button>
            );
          })}
        </div>
        {hint && <p role="status" className="mt-1.5 text-xs font-semibold text-[#9F1239]">{hint}</p>}
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">Status</p>
        <div className="inline-flex rounded-full border border-gray-300 bg-white p-0.5" role="group" aria-label="Module status">
          {(['draft', 'published'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={status === s}
              onClick={() => { if (status !== s) onStatus(s); }}
              className={`min-h-[44px] px-4 rounded-full text-xs font-semibold transition ${
                status === s ? 'bg-[#DC143C] text-white' : 'text-gray-600 hover:text-[#9F1239]'
              }`}
            >
              {s === 'draft' ? 'Draft' : 'Published'}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-gray-500">
        {inLearn
          ? 'Shown in Learn for tenants (still subject to linked packages).'
          : 'Not in Learn yet — tenants see it once it is Published and for Tenants or Tenant employees.'}
      </p>
    </div>
  );
}
